import storyHtml from './story.html';

import {Home} from '../home/home';
import {emitter, EVENTS} from '../../../utils/emitter';
import {TweenMax, Power2, ScrollToPlugin} from 'gsap/all';
import {partial} from '../../../utils/partial';
import {Window} from '../../../utils/controllers/window';
import {rootPath, phoneBoundary, getLanguage} from '../../../configs/global';

// Story Modules
import {AuthorModule} from './authorModule/authorModule';
import {LeadModule} from './leadModule/leadModule';
import {ChapterModule} from './chapterModule/chapterModule';
import {TextModule} from './textModule/textModule';
import {ImageGalleryModule} from './imageGalleryModule/imageGalleryModule';
import {QuoteModule} from './quoteModule/quoteModule';
import {FullImageModule} from './fullImageModule/fullImageModule';
import {DonateModule} from './donateModule/donateModule';
import {VideoModule} from './videoModule/videoModule';
import {AudioModule} from './audioModule/audioModule';

// Config files
import storyCesar from '../../../configs/storyCesar';
import storyZahraa from '../../../configs/storyZahraa';
import storyShervin from '../../../configs/storyShervin';
import storyBorderlands from '../../../configs/storyBorderlands';
import storyBadHombre from '../../../configs/storyBadHombre';
import storyTwinCities from '../../../configs/storyTwinCities';

export class Story {
  public id: String;
  public element: HTMLElement;
  public home: Home;
  private images: Array<Node>;
  private modules: Array<any> = [];
  private donationModule: any;
  private language = getLanguage();
  private storyTemplates: Object = {
    'story-cesar': storyCesar[this.language],
    'story-zahraa': storyZahraa[this.language],
    'story-shervin': storyShervin[this.language],
    'story-borderlands': storyBorderlands[this.language],
    'story-bad-hombre': storyBadHombre[this.language],
    'story-twin-cities': storyTwinCities[this.language]
  };

  constructor(home: Home, params: any) {
    this.element = partial(storyHtml, '#story');

    this.home = home;
    this.home.storyPlaceholder.appendChild(this.element);

    if (this.storyTemplates[params.story]) {
      this.id = params.story;
    } else {
      // If the ID passed is not a valid story
      this.id = 'story-cesar'; //TODO: update this to be dynamic
    }

    // Prevent webpack tree shaking
    ScrollToPlugin;

    // Build out story modules from templates
    const moduleData = this.storyTemplates[this.id as string].modules;
    for (let i = 0; i < moduleData.length; i++) {
      let module;
      switch (moduleData[i].template) {
        case 'textModule':
          module = new TextModule(this.element, moduleData[i]);
          break;
        case 'imageGalleryModule':
          module = new ImageGalleryModule(this.element, moduleData[i]);
          break;
        case 'quoteModule':
          module = new QuoteModule(this.element, moduleData[i]);
          break;
        case 'leadModule':
          module = new LeadModule(this.element, moduleData[i]);
          break;
        case 'chapterModule':
          module = new ChapterModule(this.element, moduleData[i]);
          break;
        case 'authorModule':
          module = new AuthorModule(this.element, moduleData[i]);
          break;
        case 'fullImageModule':
          module = new FullImageModule(this.element, moduleData[i]);
          break;
        case 'donateModule':
          module = new DonateModule(this.element, moduleData[i]);
          break;
        case 'videoModule':
          module = new VideoModule(this.element, moduleData[i]);
          break;
        case 'audioModule':
          module = new AudioModule(this.element, moduleData[i]);
          break;
      }
      if (moduleData[i].template === 'donateModule') {
        this.donationModule = module;
      }
      if (module) {
        this.modules.push(module);
      }
    }

    // Give preloader boxes dimension
    this.images = Array.from(this.element.querySelectorAll('[data-src]'));

    for (let i = this.images.length - 1; i >= 0; i--) {
      const img = this.images[i] as HTMLElement;
      let h = Number(img.getAttribute('data-height'));
      let w = Number(img.getAttribute('data-width'));
      img.parentElement.style.paddingBottom = (h / w) * 100 + '%';
    }

    this.addHandlers();
  }

  private addHandlers() {
    this.home.scrollContainer.addEventListener('scroll', this.onScroll);
    emitter.on(EVENTS.resize, this.onResize);
  }

  private onResize = () => {
    this.onScroll();
  };

  private onScroll = () => {
    const scrollTop = this.home.scrollContainer.scrollTop;
    const video = this.home.carousel.getCurrentItem().videoPlayer;
    const smallDevice = Window.width < phoneBoundary ? true : false;
    this.checkImages();

    // Video
    if (video && scrollTop > this.element.offsetTop) {
      if (!video.videoElement.paused) video.pause();
    }

    // Color shifting
    if (scrollTop === 0 && smallDevice) {
      this.home.header.animateToWhite();
    } else if (smallDevice) {
      this.home.header.animateToBlack();
    } else if (scrollTop > this.element.offsetTop - 220 && !smallDevice) {
      this.home.header.animateToBlack();
    } else if (!smallDevice) {
      this.home.header.animateToWhite();
    }

    // Header scroll handler
    this.home.header.onScroll(scrollTop, this.home.bottomOfPage);

    // Change header for desktop donation module
    const donationRect = this.donationModule.element.getBoundingClientRect();
    if (Math.round(donationRect.bottom) <= 0 && !smallDevice) {
      this.home.header.animateToBlack();
    } else if (Math.round(donationRect.top) < 0 && !smallDevice) {
      this.home.header.animateToWhite();
    }

    // Background color for mobile
    if (scrollTop < Window.height * 0.6 && smallDevice) {
      this.home.header.changeBackgroundTransparency(0);
    } else if (smallDevice) {
      this.home.header.changeBackgroundTransparency(1);
    }
  };

  private checkImages() {
    // Lazy load images
    for (let i = this.images.length - 1; i >= 0; i--) {
      const img = this.images[i] as HTMLElement;
      const pos = img.getBoundingClientRect().top;
      if (pos < Window.height * 0.75) {
        this.loadImage(img);
        this.images.splice(i, 1);
      }
    }
  }

  private loadImage(img) {
    img.onload = function() {
      TweenMax.to(img, 0.7, {opacity: 1});
    };
    img.setAttribute('src', rootPath + img.getAttribute('data-src'));
  }

  public animateIn() {
    TweenMax.set(this.element, {opacity: 0});
    TweenMax.to(this.element, 1, {opacity: 1});
    this.home.footer.animateOut();
  }

  public animateOutScroll(delay = 0) {
    this.home.scrollContainer.removeEventListener('scroll', this.onScroll);
    emitter.off(EVENTS.resize, this.onResize);
    return new Promise(resolve => {
      TweenMax.to(this.element, 1, {opacity: 0});
      TweenMax.to(this.home.scrollContainer, 1, {
        scrollTo: {y: 0},
        ease: Power2.easeInOut,
        onComplete: () => {
          resolve();
          this.kill(); //
        }
      });
    });
  }

  public kill() {
    this.home.scrollContainer.removeEventListener('scroll', this.onScroll);
    emitter.off(EVENTS.resize, this.onResize);
    for (let i = 0; i < this.modules.length; i++) {
      if (this.modules[i].kill) {
        this.modules[i].kill();
      }
    }
    this.element.remove();
  }
}
